import { useQuery } from '@apollo/client/react';
import { MIN_TOKEN_LENGTH_PG_TRGM, shortTokensDropped, tokenize } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { useCallback, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { type Pageable, type RequestFilter, SortOrder } from '../graphql/generated/graphql.ts';
import { useCurrentContextStore } from '../hooks/store/useCurrentContextStore.ts';
import type { UpdateEvent } from '../socket/types.ts';
import { useSubscriptions } from '../socket/useSubscription.ts';
import { SENT_REQUESTS_QUERY } from './SentRequests.gql';

const defaultPageable: Pageable = {
  sortBy: 'sentAt',
  sortOrder: SortOrder.DESC,
  offset: 0,
  limit: 10,
};

/** Filter, paging and search of the sent-requests list, reloaded when its Anforderungen change. */
export const useSentRequestsList = () => {
  const { currentDoctorId } = useCurrentContextStore();
  const [filter, setFilter] = useState<RequestFilter>({ search: '', from: null, to: null, birthday: null });
  const [pageable, setPageable] = useState<Pageable>(defaultPageable);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebouncedCallback(value => setFilter({ ...filter, search: value }), 300);
  const validBackendTokens = tokenize(filter.search, { minTokenLength: MIN_TOKEN_LENGTH_PG_TRGM });

  const { data, previousData, loading, refetch } = useQuery(SENT_REQUESTS_QUERY, {
    variables: {
      doctorId: currentDoctorId,
      pageable: pageable,
      // Send only tokens that pass the backend's pg_trgm minimum length.
      filter: { ...filter, search: validBackendTokens.join(' ') },
    },
    skip: filter.search.trim() !== '' && validBackendTokens.length === 0,
  });

  const onUpdate = useCallback(
    (event: UpdateEvent) => {
      console.debug(`received UI update: ${JSON.stringify(event)}`);
      refetch();
    },
    [refetch]
  );
  useSubscriptions(
    [
      { type: 'request-sent', tenantId: currentDoctorId },
      { type: 'order-received', tenantId: currentDoctorId },
      { type: 'request-reordered', tenantId: currentDoctorId },
      { type: 'request-comment-updated', tenantId: currentDoctorId },
    ],
    onUpdate
  );

  // reset pageable if state is invalid, e.g. when searching on last page
  if (data?.sentRequests && data.sentRequests.entries.length === 0 && pageable.offset > 0) {
    setPageable({ ...pageable, offset: 0 });
  }
  const page = data?.sentRequests ?? previousData?.sentRequests;

  return {
    currentDoctorId,
    data,
    entries: page?.entries ?? [],
    hasMore: page?.hasMore ?? false,
    loading,
    refetch,
    filter,
    setFilter,
    pageable,
    setPageable,
    searchValue,
    setSearchValue,
    debouncedSearch,
    validBackendTokens,
    droppedTokens: shortTokensDropped(filter.search),
  };
};
